<template>
  <div class="order-detail-container">
    <div class="feedback">
      <transition name="fade" @enter="enterSuccess">
        <FeedbackSuccess v-if="isShowableSuccess" :message="successMessage" />
      </transition>
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="isShowableError" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="orderLoading" class="order-detail__loading">
      <Loading />
    </div>
    <section v-else class="order-detail__section">
      <div v-if="!isApp">
        <PageHeadline title="購入履歴の詳細" />
        <div class="order-detail__gray-border" />
      </div>
      <div class="order-detail__body">
        <div class="order-detail-box">
          <div class="order-detail-box__contents">
            <div class="contents__order">
              <div class="contents__header">
                <div class="header__created-at">
                  <div class="minimal">
                    {{ order.formatted_created_at }}
                  </div>
                </div>
                <div class="header__order-detail-code">
                  <div class="minimal">
                    {{ `ご注文番号: ${order.order_code}` }}
                  </div>
                </div>
              </div>
              <OrderHistoryWishListInfo
                :showable="order.wish_list_user_showable"
                :icon-url="order.wish_list_user_icon_url"
                :nickname="order.wish_list_user_nickname"
              />
            </div>
            <div v-if="!order.personal_use">
              <div class="contents__gift-card">
                <div class="contents__card-design">
                  <img :src="order.card_design_image_url" />
                </div>
                <div v-if="order.has_message" class="contents__message">
                  <div class="message_block">
                    <MinMessage :message="order.message" />
                  </div>
                </div>
                <div class="contents__sender-name">
                  贈り主名: {{ order.sender_name }}
                </div>
              </div>
            </div>
            <div v-if="ticketsLoading" class="order-detail__small-loading">
              <SmallLoading />
            </div>
            <div
              v-for="(ticket, index) in tickets"
              v-else
              :key="index"
              class="contents__ticket"
            >
              <TicketDetail
                :link-url="ticket.item_sku_url"
                :image-url="ticket.item_sku_image"
                :item-id="ticket.item_id"
                :item-name="ticket.item_name"
                :item-sku-id="ticket.item_sku_id"
                :item-sku-name="ticket.item_sku_name"
                :brand-name="ticket.brand_name"
                :expired-date="ticket.formatted_usable_limit_at"
                :price="ticket.price_with_shipping_cost"
                :is-including-tax="ticket.including_tax"
                :is-ticket-disabled="false"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="order-detail__body--for-accounting-info">
        <div class="order-detail-box">
          <div class="order-detail-box__accounting-info">
            <div class="accounting-info__ticket-number body-bold">
              <div class="ticket-number__label">合計枚数</div>
              <div class="ticket-number__value">
                {{ order.item_sku_contents_count }}
              </div>
            </div>
            <div
              v-if="order.discounted"
              class="accounting-info__discount-price caption"
            >
              <div class="accounting-info__discount-price__label">
                ギフトコード利用
              </div>
              <div class="accounting-info__discount-price__value">
                - ¥{{ order.delimited_discount_price }}
              </div>
            </div>
            <div class="accounting-info__payment-amount body-bold">
              <div class="payment-amount__label-block">
                <div class="payment-amount__label">決済合計</div>
                <div
                  v-if="order.showable_include_tax_display"
                  class="payment-amount__label-caption"
                >
                  (税込)
                </div>
              </div>
              <div class="payment-amount__value">
                ¥{{ order.delimited_payment_price }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="order.personal_use">
        <div class="order-detail__gray-border" />
        <div class="order-detail__usable-gift">
          <button
            class="btn-multi btn-multi--top chevron--forward--black order-detail__usable-gift__button"
            @click="onClickUsableGift"
          >
            使えるギフトから利用する
          </button>
          <button
            class="btn-multi btn-multi--inner chevron--forward--black"
            @click="copyFromGiftCard(true)"
          >
            同じ商品を自分用に再購入する
          </button>
          <button
            class="btn-multi btn-multi--bottom chevron--forward--black"
            @click="copyFromGiftCard(false)"
          >
            同じ商品をギフトとして贈る
          </button>
        </div>
      </div>
      <div v-else>
        <div class="order-detail__gray-border" />
        <div class="order-detail__gift-url">
          <div class="body-bold">ギフトURL</div>
          <div class="gift-code-form">
            <input
              class="gift-code-field--inputted"
              type="text"
              :value="order.gift_card_full_url"
              readonly
            />
            <div class="gift-code-submit" @click="onClickGiftUrl">コピー</div>
          </div>
        </div>
        <div v-if="!order.wish_list_user_showable">
          <div class="order-detail__gray-border" />
          <div class="order-detail__usable-gift">
            <button
              class="btn-multi btn-multi--single chevron--forward--black"
              @click="copyFromGiftCard(false)"
            >
              同じ内容でギフトカードの下書きを作成する
            </button>
          </div>
        </div>
      </div>
      <div v-if="order.wish_list_user_showable">
        <div class="order-detail__gray-border" />
        <div class="order-detail__wish-list">
          <button
            class="btn-multi btn-multi--single chevron--forward--black order-detail__wish-list__button"
            @click="onClickWishList(order.wish_list_url, isApp)"
          >
            <div class="wish-list-button__nickname">
              {{ order.wish_list_user_nickname }}
            </div>
            <div class="wish-list-button__text">
              さんのほしいものリストを見る
            </div>
          </button>
        </div>
      </div>
      <div v-if="!isApp" class="order-detail__footer">
        <div class="order-detail__footer-link">
          <a
            :href="mypageOrderHistoriesPath"
            class="text-link--sub tl-link-chevron-back--left"
          >
            購入履歴に戻る
          </a>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import axios from 'axios'
import Loading from '../../atoms/loadings/Loading.vue'
import SmallLoading from '../../atoms/loadings/SmallLoading.vue'
import TicketDetail from '../../../components/atoms/tickets/TicketDetail.vue'
import FeedbackSuccess from '../../../components/atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../../components/atoms/feedbacks/feedback-error.vue'
import PageHeadline from '../../../components/molecules/headlines/PageHeadline.vue'
import MinMessage from '../../../components/molecules/messages/MinMessage.vue'
import OrderHistoryWishListInfo from '../../../components/organism/mypages/order_histories/OrderHistoryWishListInfo.vue'

export default {
  components: {
    Loading,
    SmallLoading,
    TicketDetail,
    FeedbackSuccess,
    FeedbackError,
    PageHeadline,
    MinMessage,
    OrderHistoryWishListInfo
  },
  props: {
    isApp: {
      type: Boolean,
      required: false,
      default: false
    },
    orderCode: {
      type: String,
      required: false,
      default: ''
    },
    mypageOrderHistoriesPath: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      successMessage: 'ギフトURLをコピーしました',
      isShowableSuccess: false,
      orderLoading: true,
      order: {},
      ticketsLoading: true,
      tickets: [],
      errorMessage: '',
      isShowableError: false
    }
  },
  computed: {
    sourceGiftCardUsageWay() {
      return this.order.personal_use ? 'self_use' : 'gift_use'
    }
  },
  mounted() {
    this.getOrder()
    this.getTickets()
  },
  methods: {
    async getOrder() {
      this.orderLoading = true
      try {
        const res = await axios.get(
          `/api/v1/users/@me/order_histories/${this.orderCode}`
        )
        this.order = {
          id: res.data.data.attributes.id,
          formatted_created_at: res.data.data.attributes.formatted_created_at,
          order_code: res.data.data.attributes.order_code,
          wish_list_user_showable:
            res.data.data.attributes.wish_list_user_showable,
          wish_list_user_icon_url:
            res.data.data.attributes.wish_list_user_icon_url,
          wish_list_user_nickname:
            res.data.data.attributes.wish_list_user_nickname,
          personal_use: res.data.data.attributes.personal_use,
          has_message: res.data.data.attributes.has_message,
          message: res.data.data.attributes.message,
          sender_name: res.data.data.attributes.sender_name,
          item_sku_contents_count:
            res.data.data.attributes.item_sku_contents_count,
          discounted: res.data.data.attributes.discounted,
          delimited_discount_price:
            res.data.data.attributes.delimited_discount_price,
          showable_include_tax_display:
            res.data.data.attributes.showable_include_tax_display,
          delimited_payment_price:
            res.data.data.attributes.delimited_payment_price,
          gift_card_full_url: res.data.data.attributes.gift_card_full_url,
          wish_list_url: res.data.data.attributes.wish_list_url,
          card_design_image_url: res.data.data.attributes.card_design_image_url,
          gift_card_id: res.data.data.attributes.gift_card_id
        }
        this.orderLoading = false
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isShowableError = true
      }
    },
    async getTickets() {
      this.ticketsLoading = true
      try {
        const res = await axios.get(
          `/api/v1/users/@me/order_histories/${this.orderCode}/tickets`
        )
        this.tickets = res.data.data.map(ticket => {
          return {
            item_sku_id: ticket.attributes.item_sku_id,
            item_sku_url: ticket.attributes.item_sku_url,
            item_sku_image: ticket.attributes.item_sku_image,
            item_id: ticket.attributes.item_id,
            item_name: ticket.attributes.item_name,
            item_sku_name: ticket.attributes.item_sku_name,
            current_item_sku_accounting_condition_id:
              ticket.attributes.current_item_sku_accounting_condition_id,
            brand_name: ticket.attributes.brand_name,
            formatted_usable_limit_at:
              ticket.attributes.formatted_usable_limit_at,
            price_with_shipping_cost:
              ticket.attributes.price_with_shipping_cost,
            including_tax: ticket.attributes.including_tax
          }
        })
        this.ticketsLoading = false
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isShowableError = true
      }
    },
    onClickUsableGift() {
      window.location.href = '/mypage/gifts'
    },
    onClickWishList(wishListUrl, isApp) {
      if (!isApp) {
        dataLayer.push({
          event: 'select_wishlist',
          button: 'order_history_detail'
        })
      } else {
        logEvent('select_wishlist', {
          button: 'order_history_detail'
        })
      }
      location.href = wishListUrl
    },
    onClickGiftUrl() {
      const giftUrl = document.querySelector(
        'input.gift-code-field--inputted'
      ).value
      const result = navigator.clipboard.writeText(giftUrl)
      document.getSelection().empty()
      if (result) {
        this.isShowableSuccess = true
      }
    },
    enterSuccess() {
      setTimeout(() => {
        this.isShowableSuccess = false
      }, 3000)
    },
    enterError() {
      setTimeout(() => {
        this.isShowableError = false
      }, 3000)
    },
    async copyFromGiftCard(personalUseForNewGiftCard) {
      // アプリは個別に処理を行う（アプリ側に処理を実装）
      if (this.isApp) {
        this.copyFromGiftCardForApp(personalUseForNewGiftCard)
        return
      }

      try {
        const res = await axios.post(
          '/api/v1/users/@me/gift_card_sessions/copy_from_gift_card',
          {
            gift_card_session: {
              gift_card_id: this.order.gift_card_id,
              personal_use_for_new_gift_card: personalUseForNewGiftCard
            }
          }
        )
        dataLayer.push({
          event: 'repeat_purchase_button',
          via: 'order_history_detail',
          source_gift_card_usage_way: this.sourceGiftCardUsageWay,
          target_gift_card_usage_way: this.targetGiftCardUsageWay(
            personalUseForNewGiftCard
          ),
          gift_card_session_id: res.data.gift_card_session_id,
          source_gift_card_order_id: this.order.id
        })
        location.href = personalUseForNewGiftCard
          ? `/draft_orders/${res.data.draft_order_id}`
          : '/gift_card/edit'
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.isShowableError = true
      }
    },
    copyFromGiftCardForApp(personalUseForNewGiftCard) {
      logEvent('repeat_purchase_button', {
        via: 'order_history_detail',
        source_gift_card_usage_way: this.sourceGiftCardUsageWay,
        target_gift_card_usage_way: this.targetGiftCardUsageWay(
          personalUseForNewGiftCard
        )
      })
      const path = personalUseForNewGiftCard
        ? `/draft_orders/xxxxxxxxxx` // 本来は draft_order_id を使うが、アプリでは利用しないので適当な値を設定する
        : '/gift_card/edit'
      const query = personalUseForNewGiftCard
        ? new URLSearchParams({
            // 自家需要の場合のみなので ticket の 1 件目を取得している
            item_sku_id: this.tickets[0].item_sku_id,
            // TODO(tsuru): item_sku_accounting_condition_idは、アプリ側で取得するようにしたので、強制アップデートが入れば削除したい
            item_sku_accounting_condition_id:
              this.tickets[0].current_item_sku_accounting_condition_id,
            order_id: this.order.id
          })
        : new URLSearchParams({
            order_code: this.orderCode,
            order_id: this.order.id
          })
      location.href = `${path}?${query.toString()}`
    },
    targetGiftCardUsageWay(personalUseForNewGiftCard) {
      return personalUseForNewGiftCard ? 'self_use' : 'gift_use'
    }
  }
}
</script>
<style lang="scss" scoped>
.order-detail-container {
  text-align: left;
  background-color: $white;

  .order-detail {
    &__head {
      margin: 32px auto;
      text-align: center;
      max-width: 872px;
    }

    &__title {
      padding: 0 16px;
    }

    &__gray-border {
      height: 16px;
      background-color: $gray-3;
    }

    &__gray-border--thin {
      height: 1px;
      background-color: $gray-2;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 48px 0 40px;
      background-color: white;
    }

    &__small-loading {
      display: flex;
      justify-content: center;
    }

    &__body {
      max-width: $small-contents-width;
      margin: 0 auto;
      padding: 0 16px;
    }

    &__body--for-accounting-info {
      max-width: $small-contents-width;
      margin: 0 auto;
      padding: 0 16px;
      border-top: 1px solid $gray-2;
    }

    &__gift-url {
      max-width: $small-contents-width;
      margin: 0 auto;
      padding: 16px;

      .body-bold {
        padding-bottom: 16px;
      }

      .gift-code-form {
        margin: auto;
      }

      .gift-code-field--inputted {
        overflow: hidden;
        text-overflow: ellipsis;
        flex: auto;
      }

      .gift-code-submit {
        width: 56px;
        text-align: center;
      }
    }

    &__usable-gift {
      max-width: $small-contents-width;
      margin: 0 auto;
      padding: 16px;
    }

    &__wish-list {
      max-width: $small-contents-width;
      margin: 0 auto;
      padding: 16px;

      &__button {
        display: flex;
      }
    }
  }

  .wish-list-button {
    &__nickname {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &__text {
      padding-right: 6px;
    }
  }

  .order-history-wish-list-info {
    margin-bottom: 16px;
  }

  .order-detail-box__contents {
    .contents {
      &__order {
        display: flex;
        flex-direction: column;
        padding-top: 16px;
      }

      &__header {
        justify-content: space-between;
        display: flex;
        align-items: center;
        margin-bottom: 16px;
      }

      &__gift-card {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-bottom: 16px;
      }

      &__card-design {
        width: 100%;

        img {
          width: 100%;
        }
      }

      &__sender-name {
        @extend %minimal;
        text-align: right;
        color: $black;
      }

      &__ticket {
        padding: 16px 0;
        border-top: 1px solid $gray-2;
      }
    }
  }

  .order-detail-box__accounting-info {
    .accounting-info__ticket-number,
    .accounting-info__discount-price,
    .accounting-info__payment-amount {
      justify-content: space-between;
      display: flex;
      margin-bottom: 16px;
    }

    .accounting-info__ticket-number {
      margin-top: 16px;
    }

    .payment-amount__label-block {
      display: flex;
      align-items: center;
    }

    .payment-amount__label {
      @extend %body-bold;
      margin-right: 8px;
    }

    .payment-amount__label-caption {
      font-size: 1.4rem;
      line-height: 1.6;
      color: $black;
      font-weight: normal;
    }

    .accounting-info__discount-price {
      &__value {
        color: $primary;
      }
    }
  }

  .order-detail__footer {
    background-color: $gray-3;
  }

  .order-detail__footer-link {
    max-width: $small-contents-width;
    margin: 0 auto;
    padding: 16px 16px 40px 16px;

    .tl-chevron-back--left {
      background-color: transparent;
    }

    .text-link--sub {
      display: inline;
    }
  }
}
</style>
